import {
  authoredScoreCeiling,
  headlineScore,
} from "../utils/diagnosticScoring";
import { bandLabels, stageIndexFromBands, stageLabel } from "../utils/stages";
import {
  FALLBACK_TRADE_OPTIONS,
  isQuestionApplicable,
  tradeLabels,
} from "../utils/trades";
import type {
  DiagnosticFinalOutcome,
  DiagnosticQuestion,
  DiagnosticResponseRow,
  DiagnosticResultEnvelope,
  DiagnosticTradeOption,
  DiagnosticVersionTree,
} from "../types/diagnostic";

/**
 * Flat, presentation-ready shape the PDF renders. Keeping the document free of
 * API types means this mapper is the only place that has to know how an
 * attempt is stored, and the document stays a pure function of its input.
 */
export interface DiagnosticReportData {
  diagnosticName: string;
  versionNumber: number;
  /**
   * Who the diagnostic is about — always the company. There is no member-facing
   * surface and no second name to print, so labs' separate `companyName` line
   * (which both callers passed as null) is gone rather than left dead.
   */
  subjectName: string;
  /** Pre-formatted, so the document does no date work of its own. */
  completedOn: string;
  stageLabel: string;
  /** Position on the ladder, for colouring. Null when no stage was placed. */
  stageIndex: number | null;
  /**
   * Trades assessed, e.g. "HVAC, Plumbing". Empty for a diagnostic that asks
   * nothing trade-specific. Printed because the placement only means something
   * against the trades it covered.
   */
  tradeNames: string[];
  outcomeLabel: string;
  /**
   * The headline number: a gated diagnostic's secondary score, an ungated
   * one's weighted total.
   *
   * Null means *nothing has been scored yet* — no scored section has
   * contributed any weight — which D15 made the engines distinguish from a
   * genuine `0`. It has to survive to here: `result.weightedTotal` is
   * `secondaryScore ?? 0`, so reading that instead printed 0.00 for a coach
   * who had answered only the gate, and the prose then explained a demotion
   * from a score that does not exist. `headlineScore()` is the one way to read
   * it that keeps the null for gated and ungated instruments alike.
   */
  secondaryScore: number | null;
  /** Full scale the score bars and the headline score are drawn against. */
  scoreCeiling: number;
  coachNotes: string | null;
  gate: {
    gateStageLabel: string;
    adjustment: "up" | "down" | "none";
  } | null;
  /**
   * `sectionId` and `id` below are carried for one reason: they are what the
   * document keys its lists on. `name` is authored by hand and nothing stops
   * an instrument having two sections called "Financials" — a key collision
   * React documents as unsupported, with children "duplicated and/or
   * omitted", on a page whose reader has no way of telling a section went
   * missing.
   */
  breakdown: {
    sectionId: number;
    name: string;
    weight: number;
    average: number | null;
  }[];
  sections: {
    id: number;
    name: string;
    weight: number;
    isGate: boolean;
    questions: {
      number: number;
      text: string;
      helpText: string | null;
      /** Chosen option, or null when the question was left unanswered. */
      answer: string | null;
    }[];
  }[];
}

export interface BuildReportInput {
  diagnosticName: string;
  versionNumber: number;
  /** The company being assessed. */
  subjectName: string;
  completedAt: string | null;
  coachNotes: string | null;
  version: DiagnosticVersionTree;
  responses: DiagnosticResponseRow[];
  result: DiagnosticResultEnvelope;
  /**
   * The stage recorded when the attempt was completed, and the single most
   * important input here.
   *
   * The PDF is a lasting artifact — the business owner keeps it — so it must
   * print the stage the coach showed them, not one recomputed at download time.
   * `result` is recomputed on every read, and `trades` was added to the schema
   * after the fact and defaults to `[]`, which makes every trade-tagged
   * question inapplicable and can silently re-place a historical attempt. Labs
   * had no such input at all and derived the stage live on every generation, so
   * the same completed attempt could print two different stages.
   *
   * Null for a draft, where there is nothing recorded yet and the live
   * derivation is the only truth available.
   */
  recordedStageLabel: string | null;
  /**
   * The outcome recorded at completion — `recordedStageLabel`'s twin, and read
   * the same way: the record first, the recomputation only when there is no
   * record.
   *
   * These two are printed inches apart. `stageLabel` has preferred the record
   * since BS3 while `outcomeLabel` derived from `result.finalOutcome`, so one
   * PDF could print the recorded "Lead" over "Outcome not final" — and
   * disagree with the company-history table, which has always shown the
   * recorded outcome. `result.finalOutcome` is null whenever the envelope was
   * scored as `in_progress`, which is exactly what a live preview of a
   * trades-less completed attempt reports.
   *
   * Null (or absent) for a draft, where nothing has been recorded yet.
   */
  recordedOutcome?: DiagnosticFinalOutcome | null;
  /** Trades the attempt covers. Questions of other trades are left out. */
  trades?: string[];
  /**
   * Trade key to label. The coach attempt payload serves `availableTrades`;
   * the company-detail payload does not, so callers that lack it pass nothing
   * and the shipped list is used. Prefer the server's whenever it is present —
   * it is the one that cannot drift.
   */
  tradeOptions?: DiagnosticTradeOption[];
}

/** Human-readable answer for one question, or null if unanswered. */
const answerText = (
  question: DiagnosticQuestion,
  response: DiagnosticResponseRow | undefined,
): string | null => {
  if (!response) return null;
  if (response.ordinalChoiceId != null) {
    const choice = question.choices?.find(
      (c) => c.id === response.ordinalChoiceId,
    );
    return choice ? choice.text : null;
  }
  if (response.yesNoValue != null) return response.yesNoValue ? "Yes" : "No";
  if (response.textValue) return response.textValue;
  return null;
};

/** Map a stored attempt into the report shape. */
export const buildDiagnosticReportData = (
  input: BuildReportInput,
): DiagnosticReportData => {
  const responsesByQuestion = new Map<number, DiagnosticResponseRow>();
  for (const r of input.responses) {
    responsesByQuestion.set(r.questionId, r);
  }

  const orderedSections = [...input.version.sections].sort(
    (a, b) => a.orderNumber - b.orderNumber,
  );

  const gate = input.result.gate ?? null;
  const ladder = bandLabels(input.version.bands);

  // A gated diagnostic is placed by stage; an ungated one by its band. Either
  // way this is only reached when nothing was recorded — see below.
  const derivedLabel = gate
    ? stageLabel(gate.finalStage, ladder)
    : (input.result.band?.label ?? null);

  // BS3: the recorded stage wins outright. A completed attempt prints what it
  // printed last time, whatever the recomputed envelope now says.
  const usedRecorded = input.recordedStageLabel != null;
  const printedStage =
    input.recordedStageLabel ?? derivedLabel ?? "No band matched";

  // Colour follows the printed word. Deriving the pill's position from
  // `gate.finalStage` instead would let a recorded "Grow" be painted in Lead's
  // colour. A label the ladder does not define stays uncoloured rather than
  // guessing; only a live derivation may fall back to the gate's own index.
  const stageIndex =
    stageIndexFromBands(printedStage, input.version.bands) ??
    (!usedRecorded && gate ? gate.finalStage : null);

  // Same rule as the stage above: the record wins, the recomputation is the
  // fallback for a draft that has nothing recorded.
  const printedOutcome = input.recordedOutcome ?? input.result.finalOutcome;

  const trades = input.trades ?? [];
  const tradeOptions = input.tradeOptions?.length
    ? input.tradeOptions
    : FALLBACK_TRADE_OPTIONS;

  // Only the questions this business was asked. Printing an HVAC margin
  // question with a blank answer on a plumbing-only report reads as a gap in
  // the coach's work rather than a question that never applied.
  let questionNumber = 0;
  const sections = orderedSections
    .map((section) => ({
      id: section.id,
      name: section.name,
      weight: section.weight,
      isGate: section.isGate,
      questions: [...section.questions]
        .filter((q) => isQuestionApplicable(q, trades))
        .sort((a, b) => a.orderNumber - b.orderNumber)
        .map((q) => {
          questionNumber += 1;
          return {
            number: questionNumber,
            text: q.text,
            helpText: q.helpText ?? null,
            answer: answerText(q, responsesByQuestion.get(q.id)),
          };
        }),
    }))
    .filter((section) => section.questions.length > 0);

  const gateSectionIds = new Set(
    orderedSections.filter((s) => s.isGate).map((s) => s.id),
  );

  return {
    diagnosticName: input.diagnosticName,
    versionNumber: input.versionNumber,
    subjectName: input.subjectName,
    completedOn: input.completedAt
      ? new Date(input.completedAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "Not completed",
    stageLabel: printedStage,
    stageIndex,
    tradeNames: tradeLabels(trades, tradeOptions),
    outcomeLabel:
      printedOutcome === "ready"
        ? "Ready to advance"
        : printedOutcome === "not_ready"
          ? "Not yet ready to advance"
          : "Outcome not final",
    // Keeps D15's null on both paths. `weightedTotal` collapses it to 0 and
    // cannot tell "nothing scored yet" from a real bottom-of-scale 0; the
    // ungated branch used to read it anyway, because an ungated instrument has
    // no `gate.secondaryScore` to read instead. See `headlineScore()`.
    secondaryScore: headlineScore(input.result, gateSectionIds),
    scoreCeiling: authoredScoreCeiling(input.version.sections),
    coachNotes: input.coachNotes,
    gate: gate
      ? {
          gateStageLabel: stageLabel(gate.gateStage, ladder),
          adjustment: gate.adjustment,
        }
      : null,
    // Gate sections carry no weight and would render as an empty 0% bar.
    breakdown: input.result.perSection
      .filter((ps) => ps.isScored && !gateSectionIds.has(ps.sectionId))
      .map((ps) => ({
        sectionId: ps.sectionId,
        name: ps.name,
        weight: ps.weight,
        average: ps.rawAvg,
      })),
    sections,
  };
};
